"use client";

import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import Button from "~/components/buttons/Button";
import Flex from "~/components/flex/Flex";
import { Icon, IconName } from "~/components/icons/Icon";
import { FormItemTypeEnum, TextInput } from "~/components/inputs/TextInput";
import Modal from "~/components/modal/Modal";
import Text from "~/components/texts/Text";
import { SEARCH_PARAM_RESET_PASSWORD_MODAL } from "~/constants";
import { useI18n } from "~/locales/client";
import { ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import { updateSearchParams } from "~/utils/clientUtils";
import { validatePassword } from "~/utils/passwordValidation";
import { colors } from "~/utils/tailwindUtils";
import { usePostHog } from "~/hooks/usePostHog";

/**
 * Reset Password Modal Component
 *
 * Displays a modal for resetting user password when reset-password-modal=true query param is present.
 * Requires a token (from URL or session) to reset the password.
 */
const ResetPasswordModal = () => {
  const searchParams = useSearchParams();
  const { data: session } = useSession();
  const translate = useI18n();
  const { captureEvent } = usePostHog();

  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(null);

  // Use the original magic-link token (preserved in session) for reset-password payload
  const token = session?.user?.token || searchParams.get("token");
  const userEmail = session?.user?.email || "";

  // Check if modal should be open - derive directly from searchParams
  const shouldOpen = searchParams.get(SEARCH_PARAM_RESET_PASSWORD_MODAL) === "true";

  // Password validation
  const passwordValidation = validatePassword(newPassword);
  const hasMinLength = newPassword.length >= 8;
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*]/.test(newPassword);

  // Check if passwords match
  const passwordsMatch = newPassword === repeatPassword && repeatPassword.length > 0;

  const handleClose = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_RESET_PASSWORD_MODAL);
    updateSearchParams(params);
    // Reset form
    setNewPassword("");
    setRepeatPassword("");
    setError(null);
    setPasswordMatchError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPasswordMatchError(null);

    // Validate passwords match
    if (newPassword !== repeatPassword) {
      setPasswordMatchError(translate("general.passwords-do-not-match"));
      return;
    }

    // Validate password requirements
    if (!passwordValidation.isValid) {
      setError(translate("general.password-requirements-not-met"));
      return;
    }

    if (!token) {
      setError(translate("general.reset-password-token-missing"));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: newPassword,
          token,
        }),
      });

      const responseData = await response.json();

      if (response.status >= 200 && response.status < 300) {
        captureEvent("password_reset_completed", { method: "token", status: response.status });
        // Success - close modal and remove query params
        handleClose();
      } else {
        setError(
          responseData && typeof responseData === "object" && "error" in responseData
            ? (responseData as { error: string }).error
            : responseData && typeof responseData === "object" && "message" in responseData
              ? (responseData as { message: string }).message
              : translate("general.reset-password-error")
        );
        setLoading(false);
      }
    } catch (err: unknown) {
      setError(
        err && typeof err === "object" && "message" in err
          ? (err as { message: string }).message
          : translate("general.reset-password-error")
      );
      setLoading(false);
      console.error("Reset password error:", err);
    }
  };

  if (!shouldOpen) return null;

  return (
    <Modal
      isOpen={shouldOpen}
      closeModal={handleClose}
      classname="pt-0 pb-6 md:pt-0 lg:pt-0 lg:pb-10"
      outsideClick
      size="lg"
    >
      <Flex className="flex-col gap-4 md:gap-6 w-full max-w-md mx-auto">
        {/* Title */}
        <Text className="text-xl md:text-[28px] font-bold text-gray-900">
          {translate("general.reset-password-title")}
        </Text>

        {/* Email Display (read-only) */}
        <Flex className="flex-col gap-2">
          <Text className="text-sm text-gray-600">{translate("general.reset-password-subtitle")}</Text>

          {userEmail && (
            <Flex className="py-1 px-4 gap-2 rounded-full border border-gray-100">
              <Icon name={IconName.Mail} />
              {userEmail}
            </Flex>
          )}
          <Text className="text-xs text-gray-500">{translate("general.reset-password-email-warning")}</Text>
        </Flex>

        {/* Error Message */}
        {error && (
          <Flex className="bg-red-50 border border-red-200 rounded-lg p-3">
            <Text className="text-sm text-red-700">{error}</Text>
          </Flex>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* New Password */}
          <Flex className="flex-col gap-2">
            <label htmlFor="new-password" className="text-sm font-semibold text-gray-700">
              {translate("general.new-password")}
            </label>
            <TextInput
              name="new-password"
              type={FormItemTypeEnum.Password}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={translate("general.new-password")}
              classNames="bg-gray-50 border-gray-300"
              autoComplete="new-password"
            />
          </Flex>

          {/* Repeat Password */}
          <Flex className="flex-col gap-2">
            <label htmlFor="repeat-password" className="text-sm font-semibold text-gray-700">
              {translate("general.repeat-password")}
            </label>
            <TextInput
              name="repeat-password"
              type={FormItemTypeEnum.Password}
              value={repeatPassword}
              onChange={(e) => {
                setRepeatPassword(e.target.value);
                // Clear password match error when user starts typing
                if (passwordMatchError) {
                  setPasswordMatchError(null);
                }
              }}
              placeholder={translate("general.repeat-password")}
              classNames={twMerge(
                "bg-gray-50 border-gray-300",
                passwordMatchError && "border-red-500",
                passwordsMatch && "border-green-500"
              )}
              autoComplete="new-password"
            />
            {passwordMatchError && <Text className="text-xs text-red-600">{passwordMatchError}</Text>}
          </Flex>

          {/* Password Requirements */}
          <Flex className="flex-col gap-2">
            <Flex className="flex-col gap-1.5">
              {/* 8 characters minimum */}
              <Flex className="items-center gap-2">
                {hasMinLength ? (
                  <Icon name={IconName.CheckCircle} size={16} color={colors.green[500]} />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                )}
                <Text className={twMerge("text-sm", hasMinLength ? "text-gray-600 font-medium" : "text-gray-500")}>
                  {translate("general.password-requirement-min-length")}
                </Text>
              </Flex>

              {/* 1 number */}
              <Flex className="items-center gap-2">
                {hasNumber ? (
                  <Icon name={IconName.CheckCircle} size={16} color={colors.green[500]} />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                )}
                <Text className={twMerge("text-sm", hasNumber ? "text-gray-600 font-medium" : "text-gray-500")}>
                  {translate("general.password-requirement-number")}
                </Text>
              </Flex>

              {/* 1 special character */}
              <Flex className="items-center gap-2">
                {hasSpecialChar ? (
                  <Icon name={IconName.CheckCircle} size={16} color={colors.green[500]} />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                )}
                <Text className={twMerge("text-sm", hasSpecialChar ? "text-gray-600 font-medium" : "text-gray-500")}>
                  {translate("general.password-requirement-special-character")}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          {/* Submit Button */}
          <Button
            type="submit"
            buttonStyle={ButtonTypesEnum.primary}
            text={translate("general.change-password")}
            isLoading={loading}
            disabled={loading || !newPassword || !repeatPassword || !passwordsMatch || !passwordValidation.isValid}
            buttonClassName="bg-red-500 hover:bg-red-600 rounded-lg py-2.5"
          />
        </form>
      </Flex>
    </Modal>
  );
};

export default ResetPasswordModal;
